<script setup>
import { computed, defineEmits } from "vue";
import { useRouter } from "vue-router";
import { ElMenuItem, ElSubMenu, ElIcon } from "element-plus";
import { ChartBarIcon } from "@heroicons/vue/outline";
import { getRoles } from "@/composable/users/getRoles.js";
import { useAuthStore } from "@/stores";

const emit = defineEmits(["navigate"]);

const router = useRouter();
const authStore = useAuthStore();
const { isSuperAdmin, isAccountManager, isSpecificManager, isMultiStoreManager, isRegionManager } =
  getRoles();

// Mirrors ReportMenus.vue's `hasRequiredRoles`.
const hasRequiredRoles = computed(
  () =>
    isSuperAdmin.value ||
    isAccountManager.value ||
    isSpecificManager.value ||
    isMultiStoreManager.value ||
    isRegionManager.value
);

const hasAdminDashboardRole = computed(
  () =>
    isSuperAdmin.value ||
    isAccountManager.value ||
    isMultiStoreManager.value ||
    isRegionManager.value
);

const managerDashboardRoute = computed(() => ({
  key: "manager-dashboard",
  label: authStore.is_acting_as_team_leader ? "Team Leader Dashboard" : "Manager Dashboard",
  path: authStore.is_acting_as_team_leader ? "/dashboard/team-lead" : "/manager-dashboard",
}));

const adminDashboardRoute = computed(() => {
  let label = "Admin Dashboard";
  if (authStore.is_acting_as_team_leader) {
    label = "Team Leader Dashboard";
  } else if (isMultiStoreManager.value || isRegionManager.value) {
    label = "Manager Dashboard";
  }
  return {
    key: "admin-dashboard",
    label,
    path: authStore.is_acting_as_team_leader ? "/dashboard/team-lead" : "/dashboard",
  };
});

// Mirrors ReportMenus.vue's `getOrderedRoutes`.
const routes = computed(() => {
  const base = [
    { key: "assignment-history", label: "Assignment History", path: "/reports?tab=assignment-history" },
    { key: "user-progress", label: "User Progress", path: "/reports?tab=course-overview" },
  ];
  const leaderboards = { key: "leaderboards", label: "Leaderboards", path: "/leaderboard" };

  if (!hasRequiredRoles.value) {
    return [...base, leaderboards];
  }

  const extended = [
    ...base,
    { key: "completed-content-summary", label: "Completed Content Summary", path: "/reports?tab=completed-content-summary" },
    leaderboards,
    { key: "usage-reports", label: "Usage Reports", path: "/usage-report-card" },
  ];

  if (isSpecificManager.value && !hasAdminDashboardRole.value) {
    extended.push(managerDashboardRoute.value);
  }
  if (hasAdminDashboardRole.value) {
    extended.push(adminDashboardRoute.value);
  }

  return extended;
});

const handleSelect = (route) => {
  emit("navigate");
  router.push(route.path);
};
</script>

<template>
  <el-sub-menu index="reports">
    <template #title>
      <el-icon>
        <ChartBarIcon class="w-5 h-5" />
      </el-icon>
      <span>Reports</span>
    </template>
    <el-menu-item
      v-for="route in routes"
      :key="route.key"
      :index="route.key"
      @click="handleSelect(route)"
    >
      <span class="">
        {{ route.label }}
      </span>
    </el-menu-item>
  </el-sub-menu>
</template>
